import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';

/* ── shared palette ─────────────────────────────────────────── */
const C = {
  bg:      '#111111',
  card:    '#1e2126',
  border:  'rgba(255,255,255,0.06)',
  orange:  '#f97316',
  blue:    '#f97316',
  green:   '#22c55e',
  purple:  '#f97316',
  muted:   '#4b5563',
  dim:     '#6b7280',
  text:    '#e2e8f0',
  subtext: '#94a3b8',
};

const card = {
  background: C.card,
  border: `1px solid ${C.border}`,
  borderRadius: 14,
  padding: '18px 20px',
};

const label = { fontSize: 10.5, fontWeight: 600, color: C.dim, letterSpacing: '0.08em', textTransform: 'uppercase' };
const bigNum = (color = C.orange) => ({ fontSize: 28, fontWeight: 800, color, letterSpacing: '-0.03em', lineHeight: 1 });

/* ── helper to safely parse Dates ── */
const getOrderDate = (order) => {
  if (!order.createdAt) return new Date();
  if (order.createdAt.toDate) return order.createdAt.toDate();
  return new Date(order.createdAt);
};

/* ── helper to format Hour ── */
const formatHour = (date) => {
  let hr = date.getHours();
  const ampm = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12;
  hr = hr ? hr : 12;
  return `${hr}${ampm}`;
};

const hoursList = ['8AM', '9AM', '10AM', '11AM', '12PM', '1PM', '2PM', '3PM', '4PM', '5PM', '6PM', '7PM'];
const monthsList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ── custom tooltip ─────────────────────────────────────────── */
function ChartTip({ active, payload, label: l, prefix = '€' }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e2126', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 4 }}>{l}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{prefix}{payload[0].value}</div>
    </div>
  );
}

/* ── pill badge ─────────────────────────────────────────────── */
function Pill({ label: l, color }) {
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, color,
      background: color + '18',
      border: `1px solid ${color}30`,
      borderRadius: 999, padding: '2px 9px',
    }}>{l}</span>
  );
}

/* ══════════════════════════════════════════════════════════════
   COURIER STATUS BAR
══════════════════════════════════════════════════════════════ */
export function CourierStatusBar({ orders, isDemo }) {
  const today = new Date();
  today.setHours(0,0,0,0);
  
  const todayOrders = orders.filter(o => getOrderDate(o) >= today);

  // Earning formula simulation: flat 5€ + 5% order value
  const getEarnings = (o) => 5.00 + ((parseFloat(o.total) || 0) * 0.05);

  const todayEarnings = todayOrders.reduce((sum, o) => sum + getEarnings(o), 0);
  const totalDeliveries = orders.filter(o => o.status === 'pickingup' || o.status === 'delivering' || o.status === 'completed').length;

  // Hourly Breakdown
  const courierHourly = hoursList.map(h => ({ hour: h, earn: 0 }));
  todayOrders.forEach(o => {
    const hrStr = formatHour(getOrderDate(o));
    const slot = courierHourly.find(x => x.hour === hrStr);
    if (slot) slot.earn += getEarnings(o);
  });

  const peakHour = courierHourly.reduce((a, b) => a.earn > b.earn ? a : b, { hour: '12PM', earn: 0 });

  // Map to recent orders
  const recentOrdersList = [...orders]
    .sort((a, b) => getOrderDate(b) - getOrderDate(a))
    .slice(0, 3)
    .map(o => ({
      id: o.orderId || o.id?.slice(0, 8).toUpperCase() || '#ORD-DEMO',
      merchant: o.companyName || 'Gourmet Kitchen',
      addr: o.address || 'Milano Bovisa',
      earn: getEarnings(o),
      tip: parseFloat(o.total) > 30 ? 2.50 : 0.00,
      status: (o.status || 'DELIVERED').toUpperCase()
    }));

  return (
    <div className="sb-card-main" style={{ fontFamily: "'DM Sans', sans-serif", background: C.bg, padding: 20, borderRadius: 18, color: C.text, maxWidth: '100%' }}>
      {isDemo && (
        <div style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.orange, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>💡 Showing demo/sample metrics. Take orders from the deliveries tab to view live stats!</span>
          <Pill label="DEMO MODE" color={C.orange} />
        </div>
      )}

      {/* ── top row ── */}
      <div className="sb-grid-3">

        {/* Today's earnings */}
        <div style={{ ...card, gridColumn: '1' }}>
          <div style={label}>Today's Earnings</div>
          <div style={{ ...bigNum(C.orange), marginTop: 8, marginBottom: 6 }}>€{todayEarnings.toFixed(2)}</div>
          <Pill label={isDemo ? "↑ +12% from yesterday" : "Live stats updating"} color={C.green} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16 }}>
            {[
              { k: 'Deliveries', v: totalDeliveries },
              { k: 'Online Time', v: totalDeliveries > 0 ? `${Math.max(1, Math.round(totalDeliveries * 0.6))}h 15m` : '0h 0m' },
              { k: 'Active Shift', v: 'Gold' },
            ].map(({ k, v }) => (
              <div key={k}>
                <div style={label}>{k}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: k === 'Active Shift' ? C.orange : C.text, marginTop: 4 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly bar chart */}
        <div style={{ ...card, gridColumn: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={label}>Hourly Breakdown</span>
            <Pill label={`Peak ${peakHour.hour}`} color={C.orange} />
          </div>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={courierHourly} barSize={14}>
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="earn" radius={[4, 4, 0, 0]} fill={C.muted} label={false}>
                {courierHourly.map((entry, i) => (
                  <rect key={i} fill={entry.hour === peakHour.hour && peakHour.earn > 0 ? C.orange : '#2a2a2a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 10 }}>
            {courierHourly.filter(x => x.earn > 0).slice(-4).reverse().map(({ hour, earn }) => (
              <div key={hour} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <span style={{ fontSize: 11, color: C.dim }}>{hour}</span>
                <div style={{ flex: 1, margin: '0 10px', height: 4, borderRadius: 99, background: '#2a2a2a', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(100, (earn / 30) * 100)}%`, height: '100%', background: hour === peakHour.hour ? C.orange : '#a0a0a0', borderRadius: 99 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>€{earn.toFixed(2)}</span>
              </div>
            ))}
            {courierHourly.filter(x => x.earn > 0).length === 0 && (
              <div style={{ fontSize: 11.5, color: C.dim, textAlign: 'center', padding: '10px 0' }}>No hourly records for today yet.</div>
            )}
          </div>
        </div>

        {/* Map placeholder */}
        <div style={{ ...card, gridColumn: 'auto', position: 'relative', overflow: 'hidden', minHeight: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={label}>Last Zone</span>
            <span style={{ fontSize: 11, color: C.blue, cursor: 'pointer' }}>⊙</span>
          </div>
          <div style={{ fontSize: 12, color: C.subtext, marginBottom: 10 }}>Naviglio Grande, Milan</div>
          <div style={{
            position: 'absolute', inset: 0, top: 50,
            backgroundImage: `
              linear-gradient(rgba(59,130,246,0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59,130,246,0.06) 1px, transparent 1px)
            `,
            backgroundSize: '22px 22px',
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20 }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: C.orange, boxShadow: `0 0 12px ${C.orange}` }} />
              </div>
            </div>
          </div>
          <button style={{
            position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
            background: '#2a2a2a', border: `1px solid ${C.border}`,
            borderRadius: 8, padding: '6px 14px',
            color: C.subtext, fontSize: 10.5, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.04em', whiteSpace: 'nowrap',
          }}>RE-CENTER MAP</button>
        </div>
      </div>

      {/* ── recent orders ── */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Recent Orders</span>
        </div>
        <div className="sb-table-container">
          <div className="sb-table-grid-courier">
            {['ORDER ID', 'MERCHANT', 'EARNINGS', 'TIP', 'STATUS'].map(h => (
              <div key={h} style={label}>{h}</div>
            ))}
            {recentOrdersList.map((o, idx) => (
              <React.Fragment key={o.id + idx}>
                <div style={{ fontSize: 12, color: C.dim }}>{o.id}</div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{o.merchant}</div>
                  <div style={{ fontSize: 10.5, color: C.muted }}>{o.addr}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>€{o.earn.toFixed(2)}</div>
                <div style={{ fontSize: 12, color: o.tip > 0 ? C.green : C.muted }}>
                  {o.tip > 0 ? `+€${o.tip.toFixed(2)}` : 'No Tip'}
                </div>
                <div><Pill label={o.status} color={o.status === 'CANCELLED' ? '#ef4444' : C.green} /></div>
              </React.Fragment>
            ))}
            {recentOrdersList.length === 0 && (
              <div style={{ gridColumn: 'span 5', textAlign: 'center', color: C.dim, padding: '20px 0', fontSize: 13 }}>No orders recorded yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   COMPANY STATUS BAR
══════════════════════════════════════════════════════════════ */
export function CompanyStatusBar({ orders, isDemo }) {
  const today = new Date();
  today.setHours(0,0,0,0);
  
  const nonCancelledOrders = orders.filter(o => o.status !== 'cancelled');
  const todayOrders = nonCancelledOrders.filter(o => getOrderDate(o) >= today);

  const totalRevenue = nonCancelledOrders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
  const todayRevenue = todayOrders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
  
  const activeCustomers = new Set(nonCancelledOrders.map(o => o.customerId)).size;
  const avgOrderValue = nonCancelledOrders.length > 0 ? (totalRevenue / nonCancelledOrders.length) : 0.00;

  // Daily orders chart
  const dailyOrdersData = hoursList.map(h => ({ hour: h, orders: 0 }));
  todayOrders.forEach(o => {
    const hrStr = formatHour(getOrderDate(o));
    const slot = dailyOrdersData.find(x => x.hour === hrStr);
    if (slot) slot.orders++;
  });

  const maxOrders = Math.max(...dailyOrdersData.map(d => d.orders), 0);
  const peakHourSlot = dailyOrdersData.find(d => d.orders === maxOrders && maxOrders > 0);

  // Monthly Revenue Chart
  const monthlyRevenueData = monthsList.map(m => ({ month: m, rev: 0 }));
  nonCancelledOrders.forEach(o => {
    const d = getOrderDate(o);
    const mName = monthsList[d.getMonth()];
    const slot = monthlyRevenueData.find(x => x.month === mName);
    if (slot) slot.rev += (parseFloat(o.total) || 0);
  });

  // Top Items
  const itemCounts = {};
  todayOrders.forEach(o => {
    const name = o.productName || o.itemsText?.replace(/^\d+x\s+/, '') || 'Delicious Meal';
    itemCounts[name] = (itemCounts[name] || 0) + 1;
  });

  const topItemsFormatted = Object.entries(itemCounts)
    .map(([name, count]) => ({ name, orders: count }))
    .sort((a, b) => b.orders - a.orders)
    .slice(0, 4);

  const maxItemOrders = topItemsFormatted.length > 0 ? topItemsFormatted[0].orders : 1;
  topItemsFormatted.forEach(item => {
    item.pct = Math.round((item.orders / maxItemOrders) * 100);
  });

  // Recent Company Orders
  const statusColor = { pending: C.orange, accepted: C.blue, ready: C.blue, pickingup: C.purple, delivering: C.purple, completed: C.green, cancelled: '#ef4444' };
  
  const recentCompanyOrders = [...orders]
    .sort((a, b) => getOrderDate(b) - getOrderDate(a))
    .slice(0, 4)
    .map(o => ({
      id: o.orderId || o.id?.slice(0, 8).toUpperCase() || '#ORD-DEMO',
      customer: o.customerName || 'Luca M.',
      items: o.itemsText || '1x Meal',
      total: parseFloat(o.total) || 0.00,
      status: o.status || 'pending'
    }));

  return (
    <div className="sb-card-main" style={{ fontFamily: "'DM Sans', sans-serif", background: C.bg, padding: 20, borderRadius: 18, color: C.text, maxWidth: '100%' }}>
      {isDemo && (
        <div style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.orange, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>💡 Showing demo/sample analytics. Post products and place orders using TestProduct to view live statistics!</span>
          <Pill label="DEMO MODE" color={C.orange} />
        </div>
      )}

      {/* ── top KPIs ── */}
      <div className="sb-grid-4">
        {[
          { label: "Today's Orders",    value: todayOrders.length,               sub: isDemo ? '↑ +23 vs yesterday' : 'Live orders counted', color: C.blue },
          { label: "Today's Revenue",   value: `€${todayRevenue.toFixed(2)}`,    sub: isDemo ? '↑ +8.4% vs yesterday' : 'Revenue calculated', color: C.green },
          { label: 'Active Customers',  value: activeCustomers,                  sub: 'This month', color: C.purple },
          { label: 'Avg Order Value',   value: `€${avgOrderValue.toFixed(2)}`,   sub: isDemo ? '↓ −€0.40 vs last week' : 'Lifetime average', color: C.orange },
        ].map(({ label: l, value, sub, color }) => (
          <div key={l} style={card}>
            <div style={label}>{l}</div>
            <div style={{ ...bigNum(color), fontSize: 22, marginTop: 8, marginBottom: 4 }}>{value}</div>
            <div style={{ fontSize: 10.5, color: sub.startsWith('↑') ? C.green : sub.startsWith('↓') ? '#ef4444' : C.dim }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* ── middle row ── */}
      <div className="sb-grid-2">

        {/* daily orders bar */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Orders Today</span>
            <Pill label={peakHourSlot ? `Peak: ${peakHourSlot.hour}` : 'No peak recorded'} color={C.orange} />
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <BarChart data={dailyOrdersData} barSize={12}>
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTip prefix="" />} />
              <Bar dataKey="orders" radius={[4, 4, 0, 0]}>
                {dailyOrdersData.map((entry, i) => (
                  <rect key={i} fill={entry.orders === maxOrders && maxOrders > 0 ? C.orange : '#2a2a2a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* monthly revenue area */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Monthly Revenue</span>
            <Pill label={isDemo ? "↑ +16% YoY" : "Yearly projection"} color={C.green} />
          </div>
          <ResponsiveContainer width="100%" height={130}>
            <AreaChart data={monthlyRevenueData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.orange} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={C.orange} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fontSize: 9, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="rev" stroke={C.orange} strokeWidth={2} fill="url(#revGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── bottom row ── */}
      <div className="sb-grid-bottom">

        {/* top items */}
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Top Items Today</div>
          {topItemsFormatted.map(({ name, orders: count, pct }) => (
            <div key={name} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
                <span style={{ fontSize: 11, color: C.dim, flexShrink: 0 }}>{count} orders</span>
              </div>
              <div style={{ height: 5, borderRadius: 99, background: '#2a2a2a' }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${C.orange}, ${C.orange}cc)` }} />
              </div>
            </div>
          ))}
          {topItemsFormatted.length === 0 && (
            <div style={{ fontSize: 12.5, color: C.dim, textAlign: 'center', padding: '30px 0' }}>No items ordered today.</div>
          )}
        </div>

        {/* recent orders */}
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Recent Orders</span>
          </div>
          <div className="sb-table-container">
            <div className="sb-table-grid-company">
              {['ORDER ID', 'CUSTOMER', 'ITEMS', 'TOTAL', 'STATUS'].map(h => (
                <div key={h} style={label}>{h}</div>
              ))}
              {recentCompanyOrders.map((o, idx) => (
                <React.Fragment key={o.id + idx}>
                  <div style={{ fontSize: 12, color: C.dim }}>{o.id}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{o.customer}</div>
                  <div style={{ fontSize: 12, color: C.subtext, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.items}</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>€{o.total.toFixed(2)}</div>
                  <div><Pill label={o.status.toUpperCase()} color={statusColor[o.status] || C.muted} /></div>
                </React.Fragment>
              ))}
              {recentCompanyOrders.length === 0 && (
                <div style={{ gridColumn: 'span 5', textAlign: 'center', color: C.dim, padding: '20px 0', fontSize: 13 }}>No orders recorded yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   DEFAULT EXPORT — role-locked, no switcher, 100% dynamic
══════════════════════════════════════════════════════════════ */
export default function StatusBar({ role = 'courier' }) {
  // Determine view directly from the role prop — no manual toggling
  const view = (role === 'company' || role === 'merchant') ? 'company' : 'courier';

  const [liveOrders, setLiveOrders] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth Tracker
  useEffect(() => {
    return onAuthStateChanged(auth, u => {
      setUser(u);
    });
  }, []);

  // Fetch Firestore Orders
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const ordersCol = collection(db, 'orders');

    const q = view === 'company'
      ? query(ordersCol, where('companyId', '==', user.uid), orderBy('createdAt', 'desc'), limit(100))
      : query(ordersCol, where('courierId', '==', user.uid), orderBy('createdAt', 'desc'), limit(100));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLiveOrders(docs);
      setLoading(false);
    }, (error) => {
      console.error('[StatusBar] Error fetching stats orders:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, view]);

  if (loading) {
    return (
      <div className="sb-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', background: '#080a17', color: C.subtext, gap: 12 }}>
        <span className="spinner" /> Loading Live Analytics…
      </div>
    );
  }

  return (
    <div className="sb-main" style={{ fontFamily: "'DM Sans', sans-serif", background: '#111111', minHeight: '100vh', padding: 24 }}>
      <style>{`
        .sb-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-bottom: 14px; }
        .sb-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 14px; }
        .sb-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
        .sb-grid-bottom { display: grid; grid-template-columns: 1fr 1.6fr; gap: 14px; }
        
        .sb-table-container { overflow-x: auto; -ms-overflow-style: none; scrollbar-width: none; }
        .sb-table-container::-webkit-scrollbar { display: none; }
        .sb-table-grid-courier { display: grid; grid-template-columns: 110px 150px 90px 80px 80px; gap: 8px; min-width: 550px; white-space: nowrap; }
        .sb-table-grid-company { display: grid; grid-template-columns: 100px 150px 100px 70px 90px; gap: 8px; min-width: 550px; white-space: nowrap; }

        @media (max-width: 768px) {
          .sb-main { padding: 12px !important; }
          .sb-card-main { padding: 16px !important; }
          .sb-grid-3 { grid-template-columns: 1fr; }
          .sb-grid-4 { grid-template-columns: 1fr 1fr; }
          .sb-grid-2 { grid-template-columns: 1fr; }
          .sb-grid-bottom { grid-template-columns: 1fr; }
          .sb-table-container { max-width: 100%; }
          .sb-table-grid-courier, .sb-table-grid-company { white-space: nowrap; }
        }
      `}</style>
      {view === 'courier'
        ? <CourierStatusBar orders={liveOrders} isDemo={false} />
        : <CompanyStatusBar orders={liveOrders} isDemo={false} />
      }
    </div>
  );
}

